import * as React from 'react';
import { ChevronDown, ChevronRight, Globe, Loader2, Pencil, Star, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { CurrencySelect } from '@/components/common/CurrencySelect';
import SearchableSelect from '@/components/common/SearchableSelect';
import { useCurrencyRegions, useRegionMutations } from '@/hooks/useCurrencyRegions';
import { apiMessage } from '@/lib/pricing';
import { inReadingOrder, type CurrencyRegion } from '@/lib/regions';

export default function CurrencyRegionsPage() {
  const board = useCurrencyRegions();
  const { add, patch, remove, assign, unassign, makeDefault } = useRegionMutations();

  const regions = inReadingOrder(board.data?.regions ?? []);
  const countries = board.data?.countries ?? [];

  const [editing, setEditing] = React.useState<CurrencyRegion | null>(null);
  const [creating, setCreating] = React.useState(false);
  const [name, setName] = React.useState('');
  const [currency, setCurrency] = React.useState('EUR');
  const [notes, setNotes] = React.useState('');

  const [assigning, setAssigning] = React.useState<CurrencyRegion | null>(null);
  const [chosen, setChosen] = React.useState<string[]>([]);

  const [expanded, setExpanded] = React.useState<string | null>(null);
  const [lookup, setLookup] = React.useState('');

  const nameByCode = React.useMemo(
    () => Object.fromEntries(countries.map((c) => [c.code, c.name])),
    [countries],
  );
  const assignedCount = countries.filter((c) => c.region).length;
  const fallback = regions.find((r) => r.is_default);

  const openCreate = () => {
    setEditing(null);
    setName('');
    setCurrency('EUR');
    setNotes('');
    setCreating(true);
  };

  const openEdit = (region: CurrencyRegion) => {
    setEditing(region);
    setName(region.name);
    setCurrency(region.currency);
    setNotes(region.notes);
    setCreating(true);
  };

  const save = () => {
    const body = { name: name.trim(), currency, notes: notes.trim() };
    const done = () => {
      setCreating(false);
      toast.success(editing ? 'Region saved' : 'Region created');
    };
    if (editing) {
      patch.mutate({ id: editing.id, ...body }, { onSuccess: done, onError: (e) => toast.error(apiMessage(e)) });
    } else {
      add.mutate(body, { onSuccess: done, onError: (e) => toast.error(apiMessage(e)) });
    }
  };

  const openAssign = (region: CurrencyRegion) => {
    setAssigning(region);
    setChosen(region.countries.map((c) => c.code));
  };

  const saveAssignment = () => {
    if (!assigning) return;
    assign.mutate(
      { id: assigning.id, countries: chosen },
      {
        onSuccess: (result) => {
          setAssigning(null);
          const moved = result.moved.length;
          toast.success(
            moved
              ? `${result.added.length + moved} assigned, ${moved} moved from another region`
              : `${result.added.length} assigned`,
          );
        },
        onError: (e) => toast.error(apiMessage(e)),
      },
    );
  };

  const drop = (region: CurrencyRegion) => {
    if (!window.confirm(
      `Delete ${region.name}? Its ${region.country_count} countries will be billed at ` +
      `${fallback?.name ?? 'the default region'}'s prices.`,
    )) return;
    remove.mutate(region.id, {
      onSuccess: (result) => {
        toast.success(
          result.reassigned_countries.length
            ? `Deleted. ${result.reassigned_countries.length} countries moved to ${result.to}.`
            : 'Deleted.',
        );
      },
      onError: (e) => toast.error(apiMessage(e)),
    });
  };

  const chosenMoves = chosen.filter((code) => {
    const row = countries.find((c) => c.code === code);
    return row?.region && row.region.id !== assigning?.id;
  }).length;

  return (
    <div className="space-y-6" data-testid="currency-regions-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Currency regions</h2>
          <p className="text-muted-foreground">
            A region is a price list with one currency. Every country sits in exactly one, and a
            customer's billing address decides theirs. Anything unassigned falls into the default.
          </p>
        </div>
        <Button onClick={openCreate} data-testid="add-currency-region">
          New region
        </Button>
      </div>

      <Card data-testid="currency-region-card">
        <CardHeader>
          <CardTitle>Regions</CardTitle>
          <CardDescription>
            A region priced in the same currency as the default inherits anything it does not
            price itself. A region in another currency inherits nothing, so it must price
            everything it sells.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {board.isLoading ? (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : (
            <Table data-testid="currency-region-list">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8" />
                  <TableHead>Region</TableHead>
                  <TableHead>Currency</TableHead>
                  <TableHead>Countries</TableHead>
                  <TableHead>Organisations</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {regions.map((region) => (
                  <React.Fragment key={region.id}>
                    <TableRow data-testid={`currency-region-row-${region.slug}`}>
                      <TableCell>
                        <button
                          type="button"
                          onClick={() => setExpanded(expanded === region.slug ? null : region.slug)}
                          className="text-muted-foreground"
                          aria-label="Show countries"
                          data-testid={`currency-region-expand-${region.slug}`}
                        >
                          {expanded === region.slug
                            ? <ChevronDown className="h-4 w-4" />
                            : <ChevronRight className="h-4 w-4" />}
                        </button>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-medium">{region.name}</span>
                          {region.is_default && (
                            <Badge
                              className="border-sky-200 bg-sky-50 text-sky-700"
                              data-testid={`currency-region-default-${region.slug}`}
                            >
                              default
                            </Badge>
                          )}
                        </div>
                        <code className="text-xs text-muted-foreground">{region.slug}</code>
                      </TableCell>
                      <TableCell>{region.currency}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {region.is_default
                          ? `${region.country_count} named · everything else`
                          : `${region.country_count}`}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {region.organization_count}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openAssign(region)}
                            data-testid={`currency-region-assign-${region.slug}`}
                          >
                            <Globe className="mr-2 h-4 w-4" />
                            Countries
                          </Button>
                          {!region.is_default && region.currency === 'EUR' && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-muted-foreground"
                              title="Make this the default"
                              onClick={() => makeDefault.mutate(region.id, {
                                onSuccess: () => toast.success(`${region.name} is now the default`),
                                onError: (e) => toast.error(apiMessage(e)),
                              })}
                              data-testid={`currency-region-make-default-${region.slug}`}
                            >
                              <Star className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-muted-foreground"
                            onClick={() => openEdit(region)}
                            data-testid={`currency-region-edit-${region.slug}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-red-600"
                            disabled={region.is_default}
                            title={region.is_default
                              ? 'The default region cannot be deleted'
                              : 'Delete this region'}
                            onClick={() => drop(region)}
                            data-testid={`currency-region-delete-${region.slug}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                    {expanded === region.slug && (
                      <TableRow>
                        <TableCell colSpan={6} className="bg-muted/30">
                          {region.countries.length === 0 ? (
                            <p className="text-sm text-muted-foreground">
                              {region.is_default
                                ? 'No countries named, which is usual: everything unassigned lands here.'
                                : 'No countries yet, so nobody is billed against this region.'}
                            </p>
                          ) : (
                            <div
                              className="flex flex-wrap gap-2"
                              data-testid={`currency-region-countries-${region.slug}`}
                            >
                              {region.countries.map(({ code }) => (
                                <span
                                  key={code}
                                  className="flex items-center gap-1 rounded border bg-background px-2 py-0.5 text-sm"
                                  data-testid={`currency-region-country-${region.slug}-${code}`}
                                >
                                  {nameByCode[code] ?? code}
                                  <button
                                    type="button"
                                    className="text-muted-foreground hover:text-red-600"
                                    aria-label={`Remove ${code}`}
                                    onClick={() => unassign.mutate({ id: region.id, code })}
                                    data-testid={`currency-region-country-remove-${region.slug}-${code}`}
                                  >
                                    <X className="h-3 w-3" />
                                  </button>
                                </span>
                              ))}
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card data-testid="country-region-lookup-card">
        <CardHeader>
          <CardTitle>Where a country is billed</CardTitle>
          <CardDescription data-testid="currency-region-coverage">
            {countries.length} countries · {assignedCount} assigned ·{' '}
            {countries.length - assignedCount} fall into {fallback?.name ?? 'the default region'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <SearchableSelect
            value={lookup}
            onChange={setLookup}
            placeholder="Choose a country"
            searchPlaceholder="Find a country…"
            className="max-w-sm"
            testId="country-region-lookup"
            options={countries.map((c) => ({
              value: c.code,
              label: c.name,
              keywords: c.code,
            }))}
          />
          {lookup && (
            <p className="text-sm" data-testid="country-region-lookup-result">
              {nameByCode[lookup]} →{' '}
              <span className="font-medium">
                {countries.find((c) => c.code === lookup)?.region?.name
                  ?? `${fallback?.name ?? 'the default region'} (not assigned)`}
              </span>{' '}
              ·{' '}
              {countries.find((c) => c.code === lookup)?.region?.currency
                ?? fallback?.currency}
            </p>
          )}
        </CardContent>
      </Card>

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent data-testid="currency-region-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit ${editing.name}` : 'New currency region'}</DialogTitle>
            <DialogDescription>
              Prices are set per region, so a new one starts with none until you price it.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="region-name">Name</Label>
              <Input
                id="region-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="EUR-DACH"
                data-testid="currency-region-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Currency</Label>
              <CurrencySelect
                value={currency}
                onValueChange={setCurrency}
                testId="currency-region-currency"
              />
              {editing?.has_prices && (
                <p className="text-xs text-amber-700">
                  This region has prices, so its currency is fixed. Every amount in it is
                  denominated in {editing.currency}.
                </p>
              )}
              {editing?.is_default && (
                <p className="text-xs text-muted-foreground">
                  The default region is always in EUR.
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="region-notes">Notes</Label>
              <Input
                id="region-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Why this region exists"
                data-testid="currency-region-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCreating(false)}>Cancel</Button>
            <Button
              disabled={!name.trim() || add.isPending || patch.isPending}
              onClick={save}
              data-testid="currency-region-save"
            >
              {editing ? 'Save' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={assigning !== null} onOpenChange={() => setAssigning(null)}>
        <DialogContent data-testid="currency-region-assign-dialog">
          <DialogHeader>
            <DialogTitle>Countries billed in {assigning?.name}</DialogTitle>
            <DialogDescription>
              A country belongs to one region, so choosing it here takes it from wherever it is.
            </DialogDescription>
          </DialogHeader>
          <Command className="rounded border">
            <CommandInput
              placeholder="Find a country…"
              data-testid="currency-region-country-search"
            />
            <CommandList className="max-h-72">
              <CommandEmpty>Nothing matches.</CommandEmpty>
              {countries.map((country) => {
                const picked = chosen.includes(country.code);
                const elsewhere = country.region && country.region.id !== assigning?.id;
                return (
                  <CommandItem
                    key={country.code}
                    value={`${country.name} ${country.code}`}
                    onSelect={() => setChosen((was) =>
                      was.includes(country.code)
                        ? was.filter((c) => c !== country.code)
                        : [...was, country.code],
                    )}
                    data-testid={`currency-region-country-option-${country.code}`}
                  >
                    <input type="checkbox" checked={picked} readOnly className="mr-2" />
                    <span className="flex-1">{country.name}</span>
                    {elsewhere && (
                      <span className={picked ? 'text-xs text-amber-700' : 'text-xs text-muted-foreground'}>
                        {picked ? `moves from ${country.region!.name}` : country.region!.name}
                      </span>
                    )}
                  </CommandItem>
                );
              })}
            </CommandList>
          </Command>
          <DialogFooter className="items-center justify-between sm:justify-between">
            <span className="text-sm text-muted-foreground" data-testid="currency-region-assign-count">
              {chosen.length} selected
              {chosenMoves ? ` · ${chosenMoves} move from another region` : ''}
            </span>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={() => setAssigning(null)}>Cancel</Button>
              <Button
                onClick={saveAssignment}
                disabled={assign.isPending}
                data-testid="currency-region-assign-save"
              >
                Save
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
